import {
  BreakpointObserver,
  Breakpoints,
  BreakpointState,
} from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

import { environment } from '../environments/environment';
import { FooterComponent } from './components/footer/footer.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { LayoutClassesEnum } from './models/layout-classes.enum';
import { AuthenticationService } from './modules/authentication/services/authentication.service';
import { ConfirmModalComponent } from './modules/confirm-modal/confirm-modal.component';
import { AutoLogoutService } from './services/auto-logout.service';
import { HubspotService } from './services/hubspot.service';
import { NavbarService } from './services/navbar.service';
import { ThemesService } from './services/themes.service';

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatSidenavModule,
    MatToolbarModule,
    MatButtonModule,
    SidebarComponent,
    FooterComponent,
    ConfirmModalComponent,
  ],
})
export class AppComponent implements OnInit {
  private readonly checkAppVersionInterval = 150 * 1000;
  private currentAppVersion = '';
  private chatVisibilityNavigationVersion = 0;

  constructor(
    public breakpointObserver: BreakpointObserver,
    public navbarService: NavbarService,
    public themesService: ThemesService,
    private hubspotService: HubspotService,
    public authenticationService: AuthenticationService,
    public router: Router,
    private autoLogoutService: AutoLogoutService,
    private http: HttpClient,
    private toastrService: ToastrService,
  ) {
    this.checkAppVersion();
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe([Breakpoints.Small, Breakpoints.XSmall])
      .subscribe((res: BreakpointState) => {
        let layoutCls = LayoutClassesEnum.Desktop;
        if (res?.breakpoints[Breakpoints.XSmall]) {
          layoutCls = LayoutClassesEnum.Mobile;
        } else if (res?.breakpoints[Breakpoints.Small]) {
          layoutCls = LayoutClassesEnum.Tablet;
        }

        this.navbarService.setLayout(layoutCls);
        return this.navbarService.layoutClass;
      });

    this.insertScript(environment.hubspotSrc);
    this.insertScript(environment.ingoSdkSrc);

    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(async () => {
        const path = this.router.url.split(/[?#]/)[0];
        const navigationVersion = ++this.chatVisibilityNavigationVersion;

        // Some GTM tags/variables assume a `page` string exists and call `page.split(...)`.
        // Ensure it is always present to avoid runtime errors in injected scripts.
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'spa_navigation',
          page: path,
        });

        if (!(await this.hubspotService.checkIfUseChat())) {
          return;
        }

        if (navigationVersion !== this.chatVisibilityNavigationVersion) {
          return;
        }

        try {
          const el = await this.hubspotService.getChatElement();
          if (navigationVersion !== this.chatVisibilityNavigationVersion) {
            return;
          }

          const currentPath = this.router.url.split(/[?#]/)[0];
          if (
            !this.hubspotService.skipRoutesForChat.some((r) =>
              currentPath.startsWith(r),
            )
          ) {
            el.classList.remove('hide-chat');
          } else {
            el.classList.add('hide-chat');
          }
        } catch {
          // Ignore HubSpot element lookup failures.
        }
      });
  }

  private async checkAppVersion() {
    try {
      const response = await lastValueFrom(
        this.http.head('/', {
          responseType: 'text',
          observe: 'response',
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
          },
        }),
      );
      const appVersionHash = response.headers.get('etag');

      if (
        this.currentAppVersion &&
        appVersionHash &&
        this.currentAppVersion !== appVersionHash
      ) {
        const toast = this.toastrService.warning(
          'Please refresh the page.',
          'Tongo has updated!',
          {
            enableHtml: true,
            timeOut: 999999999,
            extendedTimeOut: 999999999,
            closeButton: true,
          },
        );
        toast.onTap.subscribe(() => window.location.reload());
        return;
      }

      this.currentAppVersion = appVersionHash!;
    } catch (error) {
      if (error instanceof Error) {
        console.log(error.message);
      } else {
        console.log('Unknown error:', error);
      }
    }

    setTimeout(() => this.checkAppVersion(), this.checkAppVersionInterval);
  }

  // insert script without locking the init page render
  private insertScript(src: string): void {
    if (!src) {
      return;
    }

    const oldScript = document.getElementsByTagName('script')[0];
    const newScript = document.createElement('script');
    newScript.async = true;
    newScript.src = src;
    oldScript?.parentNode?.insertBefore(newScript, oldScript);
  }

  public showFooter(): boolean {
    const hiddenPaths = [
      '/invite-signup',
      '/login',
      '/signup',
      '/invite-agent',
      '/request-to-join-brokerage',
      '/pdf-viewer',
      '/cloc',
    ];
    return !hiddenPaths.some((path) => this.router.url.startsWith(path));
  }
}
